import application from 'application';
import serverInterface from 'server-interface';

import {
  findIndexByPropertyValue
} from 'utilities/collection';

import {
  applyTraits
} from 'utilities/javascript';
import InitializeOnce from 'utilities/javascript/initialize-once';

import internalEventCodes from 'event-system/codes/internal';

class CalendarActivityTagsManager {

  constructor(application) {

    this.initializePromise = null;
    this.tags = [];

    this.application = application;

  }

  // The screens that open with their tags already loaded hand them over here, so the manager is
  // seeded rather than fetching them a second time.
  async doInitialize(tags = null) {

    this.registerEventHandlers();

    if (tags !== null) {
      this.tags = tags;
      return;
    }

    await this.loadAll();

  }

  tryAddTag(tag) {

    const tagIndex = findIndexByPropertyValue(this.tags, '_id', tag._id);

    if (tagIndex !== -1) {
      return;
    }

    this.tags.push(tag);

  }

  tryUpdateTag(tag) {

    const tagIndex = findIndexByPropertyValue(this.tags, '_id', tag._id);

    if (tagIndex === -1) {
      return;
    }

    this.tags[tagIndex] = tag;

  }

  tryRemoveTag(tagId) {

    const tagIndex = findIndexByPropertyValue(this.tags, '_id', tagId);

    if (tagIndex === -1) {
      return;
    }

    this.tags.splice(tagIndex, 1);

  }

  handleTagCreatedEvent(payload) {

    this.tryAddTag(payload.conversationLabel);

  }

  handleTagUpdatedEvent(payload) {

    this.tryUpdateTag(payload.conversationLabel);

  }

  handleTagDeletedEvent(payload) {

    this.tryRemoveTag(payload.conversationLabelId);

  }

  registerEventHandlers() {}

  getAll() {

    return this.tags;

  }

  async loadAll() {

    if (this.application.accessController.isMemberOrAdministrator() === true) {
      this.tags = await serverInterface.startup.calendarActivities.tags.getAllTags();
    } else {
      this.tags = await serverInterface.startup.publicCalendarActivities.tags.getAllTags();
    }

  }

  async create(data) {

    try {

      const tag = await serverInterface.startup.calendarActivities.tags.createTag(data);

      this.tryAddTag(tag);

      return tag;

    } catch (exception) {

      // eslint-disable-next-line no-throw-literal
      throw { message: 'Label already exists' };

    }

  }
  handleEditLabelException(exception, value) {

    let message = 'Unknown error! Please try again!!';

    if (exception.code === 'USER_SPACE_CONVERSATION_LABEL_ALREADY_EXISTS') {

      message = `Label: ${value} already exists!`;

    } else if (exception.code === 'ValidationError') {

      const error = exception.metadata[0];

      if (error.keyword === 'minLength') {
        message = 'Please enter valid label name';
      } else if (error.keyword === 'maxLength') {
        message = 'Label name cannot exceed 20 characters';
      }

    }

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message,
      seconds: 5,
      type: 'error'
    });

  }

  async update(tagId, data) {

    try {

      const updated = await serverInterface.startup.calendarActivities.tags.updateTagById(tagId, data);

      if (updated === true) {
        this.tryUpdateTag({
          ...data,
          _id: tagId
        });
      }

      return updated;

    } catch (exception) {

      if (exception.code === 'TAG_ALREADY_EXISTS') {

        // eslint-disable-next-line no-throw-literal
        throw { alreadyExists: true };

      } else if (exception.code === 'ValidationError') {

        const error = exception.metadata[0];
        let message = null;

        if (error.keyword === 'minLength') {
          message = 'Please enter valid label name';
        } else if (error.keyword === 'maxLength') {
          message = 'Label name cannot exceed 20 characters';
        }

        // eslint-disable-next-line no-throw-literal
        throw { message };

      } else {

        // eslint-disable-next-line no-throw-literal
        throw { message: 'Unknown error. Please contact administration!' };

      }

    }

  }

  async delete(tagId) {

    const deleted = await serverInterface.startup.calendarActivities.tags.deleteTagById(tagId);

    if (deleted === true) {
      this.tryRemoveTag(tagId);
    }

    return deleted;
  }

}

export default applyTraits(CalendarActivityTagsManager, [InitializeOnce]);